import { getCsrfToken } from "@/integrations/php/client";

export type AdminBillingPeriod =
  | "current_week"
  | "current_month"
  | "previous_month"
  | "current_quarter"
  | "current_year"
  | "all"
  | "custom";

export type AdminBillingPartnerState = "all" | "with_leads" | "active" | "inactive";

export interface AdminBillingInput {
  period: AdminBillingPeriod;
  state: AdminBillingPartnerState;
  search: string;
  page: number;
  from?: string;
  to?: string;
}

export interface AdminBillingSummary {
  total_leads: number;
  billable_leads: number;
  unassigned_leads: number;
  total_amount: number;
  partners_with_leads: number;
  active_partners_with_leads: number;
  inactive_partners_with_leads: number;
  inactive_billable_leads: number;
  average_amount: number;
}

export interface AdminBillingRow {
  id: string;
  name: string;
  location: string;
  active: boolean;
  lead_count: number;
  amount: number;
  first_lead_at: string | null;
  last_lead_at: string | null;
}

export interface AdminBillingResponse {
  summary: AdminBillingSummary;
  filtered_summary: Pick<AdminBillingSummary, "billable_leads" | "total_amount" | "partners_with_leads">;
  rows: AdminBillingRow[];
  filters: { period: AdminBillingPeriod; state: AdminBillingPartnerState; q: string };
  range: { period: AdminBillingPeriod; start_date: string | null; end_date: string | null; end_exclusive: string | null; label: string };
  lead_price: number;
  pagination: { page: number; per_page: number; total: number; pages: number };
  generated_at: string;
}

const toNumber = (value: unknown): number => Number(value || 0);

const billingParams = (input: AdminBillingInput, format?: "csv"): URLSearchParams => {
  const params = new URLSearchParams({
    period: input.period,
    state: input.state,
    page: String(input.page),
    per_page: "50",
  });
  if (input.search.trim()) params.set("q", input.search.trim());
  if (input.period === "custom" && input.from) params.set("from", input.from);
  if (input.period === "custom" && input.to) params.set("to", input.to);
  if (format) params.set("format", format);
  return params;
};

export async function fetchAdminBilling(input: AdminBillingInput): Promise<AdminBillingResponse> {
  const response = await fetch(`/api/admin/billing.php?${billingParams(input).toString()}`, {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body?.error?.message || "Δεν φορτώθηκαν τα στοιχεία χρέωσης.");
  }

  const data = body.data || {};
  const summary = data.summary || {};
  const filtered = data.filtered_summary || {};
  return {
    summary: {
      total_leads: toNumber(summary.total_leads),
      billable_leads: toNumber(summary.billable_leads),
      unassigned_leads: toNumber(summary.unassigned_leads),
      total_amount: toNumber(summary.total_amount),
      partners_with_leads: toNumber(summary.partners_with_leads),
      active_partners_with_leads: toNumber(summary.active_partners_with_leads),
      inactive_partners_with_leads: toNumber(summary.inactive_partners_with_leads),
      inactive_billable_leads: toNumber(summary.inactive_billable_leads),
      average_amount: toNumber(summary.average_amount),
    },
    filtered_summary: {
      billable_leads: toNumber(filtered.billable_leads),
      total_amount: toNumber(filtered.total_amount),
      partners_with_leads: toNumber(filtered.partners_with_leads),
    },
    rows: Array.isArray(data.rows)
      ? data.rows.map((row: Record<string, unknown>) => ({
          id: String(row.id || ""),
          name: String(row.name || ""),
          location: String(row.location || ""),
          active: Boolean(row.active),
          lead_count: toNumber(row.lead_count),
          amount: toNumber(row.amount),
          first_lead_at: row.first_lead_at ? String(row.first_lead_at) : null,
          last_lead_at: row.last_lead_at ? String(row.last_lead_at) : null,
        }))
      : [],
    filters: data.filters,
    range: data.range,
    lead_price: toNumber(data.lead_price),
    pagination: {
      page: toNumber(data.pagination?.page) || 1,
      per_page: toNumber(data.pagination?.per_page) || 50,
      total: toNumber(data.pagination?.total),
      pages: toNumber(data.pagination?.pages) || 1,
    },
    generated_at: String(data.generated_at || ""),
  };
}

export async function exportAdminBillingCsv(input: AdminBillingInput): Promise<void> {
  const response = await fetch(`/api/admin/billing.php?${billingParams(input, "csv").toString()}`, {
    credentials: "include",
    headers: { "X-CSRF-Token": await getCsrfToken() },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error?.message || "Η εξαγωγή CSV δεν ολοκληρώθηκε.");
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `diathermiki-billing-${input.period}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
